import {
  AddOnConfigTypeForBackend,
  BannerConfigTypeForBackend,
  BannerSettingsTypeForBackend,
  CART_WIDTH_FOR_MOBILE,
  CartButtonCTAText,
  CartNoteType,
  CartUIConfigTypeForBackend,
  CouponPageConfigTypeForBackend,
  customizeCartUIType,
  DiscountConfigTypeForBackend,
  NotesConfigTypeForBackend,
  OrderSummaryConfigTypeForBackend,
  PrimaryCTATextTypeForBackend,
  UpsellConfigTypeForBackend
} from '../types/cartUI'
import {
  BannerStateConfigType,
  CartUIConfigType,
  CheckoutUIConfigType,
  CouponVisibilityStateConfigType,
  CustomisablePageType
} from '../types/merchantAttributes'
import {
  DEFAULT_CART_BUTTON_CTA_TEXT,
  DEFAULT_CART_NOTE_ERROR,
  DEFAULT_CART_UI_DATA,
  DEFAULT_STATE_BANNER_CONFIG,
  DEFAULT_UI_CONFIG_DATA
} from 'src/assets/CartUIConfigs'
import { BannerConfigType, BannerItemConfigType } from '../types/customizeUI'
import { analyticsApiRequestType, apiURI } from 'src/lib/utils/constants'
import { FilterConfigType } from '../types/filters'
import { extractDateFromFilters } from './analyticsHelpers'
import { AnalyticsMetricType, GenericAnalyticsWidgetUIConfigType } from '../types/analytics'
import { cartAnalyticsWidgetSet } from '../types/cart'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { addDays, endOfDay } from 'date-fns'
import { parseCouponVisibilityConfig } from './parsers'

const parseBannerConfig = (data: BannerStateConfigType[]): BannerConfigType => {
  const bannerConfig: BannerStateConfigType =
    data?.find((config: { type: CustomisablePageType }) => config.type === 'CART') ??
    data?.find((config: { type: CustomisablePageType }) => config.type === 'ORDER_SUMMARY') ??
    DEFAULT_STATE_BANNER_CONFIG
  return {
    enabled: bannerConfig?.enabled ?? false,
    color: bannerConfig?.color ?? '#000000',
    text: bannerConfig?.text ?? '',
    textColor: bannerConfig?.textColor ?? '#FFFFFF'
  }
}

const parsePrimaryCTAConfigPayload = (data: CartButtonCTAText[]) => {
  const cartButtonCTAText: CartButtonCTAText =
    data?.find((config: { type: string }) => config.type === 'CART') ?? DEFAULT_CART_BUTTON_CTA_TEXT
  return cartButtonCTAText.CTAText
}

export const parseCartUIConfig = (uiConfig: CartUIConfigType[] | undefined) => {
  if (!uiConfig) {
    return DEFAULT_CART_UI_DATA
  }

  const cartUIConfig: CartUIConfigType =
    uiConfig.find((config: { sfContext: string }) => config.sfContext === 'CART') ??
    uiConfig.find((config: { sfContext: string }) => config.sfContext === 'CHECKOUT') ??
    DEFAULT_UI_CONFIG_DATA

  const checkoutUIConfig = uiConfig.find((config: { sfContext: string }) => config.sfContext === 'CHECKOUT')

  const parsedCartUIConfig = {
    cartBannerUIConfigData: parseBannerConfig(
      cartUIConfig?.uiAttributes?.bannerConfig ?? [DEFAULT_STATE_BANNER_CONFIG]
    ),
    cartBottomBannerUIConfigData: parseBannerConfig(
      cartUIConfig?.uiAttributes?.bottomBannerConfig ?? [DEFAULT_STATE_BANNER_CONFIG]
    ),
    colorPaletteData: {
      primaryColor: checkoutUIConfig?.uiAttributes?.colorPalette?.primaryColor ?? '#000000',
      primaryTextColor: checkoutUIConfig?.uiAttributes?.colorPalette?.primaryTextColor ?? '#FFFFFF'
    },
    cartButtonCTAText: parsePrimaryCTAConfigPayload(
      cartUIConfig?.uiAttributes?.primaryCTAConfig ?? [DEFAULT_CART_BUTTON_CTA_TEXT]
    ),
    cartWidthForMobile: cartUIConfig?.uiAttributes?.cartWidthForMobile ?? CART_WIDTH_FOR_MOBILE.FULL
  }
  return parsedCartUIConfig
}

const parseCartCouponVisibilityConfig = (data: any) => {
  const parsedConfigs = parseCouponVisibilityConfig(data) || []

  const hasCartType = parsedConfigs.some((config: CouponVisibilityStateConfigType) => config?.type === 'CART')

  if (!hasCartType) {
    parsedConfigs.push(DEFAULT_CART_UI_DATA.couponVisibilityConfig[0])
  }

  return parsedConfigs
}

export const parseNewCartConfig = (config: any, checkoutUI: CheckoutUIConfigType | null): customizeCartUIType => {
  if (!config) {
    return DEFAULT_CART_UI_DATA
  }

  return {
    showUpiIconsOnCartButton: config.show_upi_icons_on_cart_button ?? false,
    enableMarketingFlag: config.layout?.enable_marketing_flag ?? false,
    cartBannerUIConfigData: (() => {
      const bannerItems: any[] = config.banner_config ?? []
      const firstItem = bannerItems[0]
      const multipleBannersEnabled = config.banner_settings?.multiple_banners_enabled ?? false
      const parsedBanners = bannerItems.map((banner: any, index: number) => ({
        id: banner.id ?? `banner-${index}`,
        position: banner.position ?? index,
        color: banner.background_color ?? '#000000',
        textColor: banner.text_color ?? '#FFFFFF',
        text: banner.html_text_string ?? ''
      }))

      return {
        enabled: config.banner_settings?.enabled ?? firstItem?.enabled ?? false,
        color: firstItem?.background_color ?? '#000000',
        text: firstItem?.html_text_string ?? '',
        textColor: firstItem?.text_color ?? '#FFFFFF',
        multipleBannersEnabled,
        animationStyle: config.banner_settings?.animation_style ?? 'vertical_ticker',
        animationDelay: config.banner_settings?.animation_delay ?? 1,
        banners: parsedBanners
      }
    })(),
    cartBottomBannerUIConfigData: {
      enabled: config.bottom_banner_config?.[0]?.enabled ?? false,
      color: config.bottom_banner_config?.[0]?.background_color ?? '#000000',
      text: config.bottom_banner_config?.[0]?.html_text_string ?? '',
      textColor: config.bottom_banner_config?.[0]?.text_color ?? '#FFFFFF'
    },
    colorPaletteData: {
      primaryColor: checkoutUI?.colorPalette?.primaryColor ?? '#000000',
      primaryTextColor: checkoutUI?.colorPalette?.primaryTextColor ?? '#FFFFFF'
    },
    cartWidthForMobile: config.cart_overlay_width ?? CART_WIDTH_FOR_MOBILE.FULL,
    couponVisibilityConfig: parseCartCouponVisibilityConfig(config?.coupon_visibility_config),
    page: {
      backgroundColor: config.page?.background_color ?? '#FFFFFF'
    },
    header: {
      backgroundColor: config.header?.background_color ?? '#FFFFFF',
      textColor: config.header?.text_color ?? '#000000'
    },
    footer: {
      backgroundColor: config.footer?.background_color ?? '#FFFFFF',
      textColor: config.footer?.text_color ?? '#000000',
      savingsColor: config.footer?.savings_color ?? '#2C874A',
      showSavings: config.footer?.show_savings ?? true
    },
    primaryCTAConfig: {
      backgroundColor: config.primary_cta_config?.[0]?.background_color,
      textColor: config.primary_cta_config?.[0]?.text_color ?? '#FFFFFF',
      borderRadius: config.primary_cta_config?.[0]?.border_radius ?? 12,
      cartButtonCTAText: config.primary_cta_config?.[0]?.cta_text ?? DEFAULT_CART_BUTTON_CTA_TEXT.CTAText
    },
    addonConfig: {
      backgroundColor: config.addon_config?.background_color ?? '#f2f2f3',
      textColor: config.addon_config?.text_color ?? '#3f3f46',
      buttonColor: config.addon_config?.button_color
    },
    orderSummaryConfig: {
      backgroundColor: config.order_summary_config?.background_color ?? '#f4f4f5',
      borderColor: config.order_summary_config?.border_color ?? '#FFFFFF',
      offerBadgeColor: config.order_summary_config?.offer_badge_color ?? '#2c874a',
      textColor: config.order_summary_config?.text_color ?? '#3F3F46',
      quantityEditorBackground: config.order_summary_config?.quantity_editor_background ?? '#FFFFFF',
      quantityEditorText: config.order_summary_config?.quantity_editor_text ?? '#4d4d4d',
      cardRadius: config.order_summary_config?.card_radius ?? 12,
      cardBorderWidth: config.order_summary_config?.card_border_width ?? 0,
      cardBorderSides: config.order_summary_config?.card_border_sides ?? 'all'
    },
    upsell: {
      isEnabled: config.upsell?.is_enabled ?? false,
      title: config.upsell?.title ?? 'Upsell',
      discountedTitle: config.upsell?.discounted_title ?? 'Discounted Upsell',
      shouldOverrideDiscountedUpsell: config.upsell?.should_override_discounted_upsell ?? false,
      backgroundColor: config.upsell?.background_color ?? '#FFFFFF',
      borderColor: config.upsell?.border_color ?? '#E4E4E7',
      textColor: config.upsell?.text_color ?? '#4d4d4d',
      buttonColor: config.upsell?.button_color ?? '#2c874a',
      offerBadgeColor: config.upsell?.offer_badge_color ?? '#2c874a',
      titleColor: config.upsell?.title_color ?? '#2b874a',
      cardRadius: config.upsell?.card_radius ?? 12,
      cardBorderWidth: config.upsell?.card_border_width ?? 1,
      cardBorderSides: config.upsell?.card_border_sides ?? 'all'
    },
    notesConfig: {
      isEnabled: config.notes_config?.is_enabled ?? false,
      backgroundColor: config.notes_config?.background_color ?? '#FFFFFF',
      borderColor: config.notes_config?.border_color ?? '#E4E4E7',
      textColor: config.notes_config?.text_color ?? '#000000',
      buttonColor: config.notes_config?.button_color ?? '#2b874a',
      cardRadius: config.notes_config?.card_radius ?? 12,
      cardBorderWidth: config.notes_config?.card_border_width ?? 1,
      cardBorderSides: config.notes_config?.card_border_sides ?? 'all'
    },
    discountConfig: {
      callout: {
        backgroundColor: config.discount_config?.callout?.background_color ?? '#FFFFFF',
        infoColor: config.discount_config?.callout?.info_color ?? '#4d4d4d',
        primaryColor: config.discount_config?.callout?.primary_color ?? '#2c874a',
        borderColor: config.discount_config?.callout?.border_color ?? '#05966933'
      },
      footer: {
        backgroundColor: config.discount_config?.footer?.background_color ?? '#2c874a0d',
        primaryColor: config.discount_config?.footer?.primary_color ?? '#2c874a',
        infoColor: config.discount_config?.footer?.info_color ?? '#3f3f46b3'
      },
      input: {
        backgroundColor: config.discount_config?.input?.background_color ?? '#FFFFFF',
        borderColor: config.discount_config?.input?.border_color ?? '#E4E4E7',
        primaryColor: config.discount_config?.input?.primary_color ?? '#6e6e6e'
      },
      button: {
        backgroundColor: config.discount_config?.button?.background_color ?? '#000000',
        textColor: config.discount_config?.button?.text_color ?? '#FFFFFF'
      },
      totalSavings: {
        primaryColor: config.discount_config?.total_savings?.primary_color ?? '#eab308',
        infoColor: config.discount_config?.total_savings?.info_color ?? '#3f3f46',
        backgroundColor: config.discount_config?.total_savings?.background_color ?? '#FFFFFF'
      },
      cardRadius: config.discount_config?.card_radius ?? 12,
      cardBorderWidth: config.discount_config?.card_border_width ?? 1,
      cardBorderSides: config.discount_config?.card_border_sides ?? 'all',
      buttonRadius: config.discount_config?.button_radius ?? 12,
      filterRadius: config.discount_config?.filter_radius ?? 12
    },
    elementOrder: config.element_order ?? ['ORDER_SUMMARY', 'DISCOUNTS', 'UPSELL', 'NOTES'],
    couponPageConfig: {
      backgroundColor: config.coupon_page_config?.background_color ?? '#FFFFFF',
      textColor: config.coupon_page_config?.text_color ?? '#FFFFFF',
      primaryColor: config.coupon_page_config?.primary_color ?? '#2b874a',
      cardRadius: config.coupon_page_config?.card_radius ?? 16,
      filterRadius: config.coupon_page_config?.filter_radius ?? 0,
      buttonRadius: config.coupon_page_config?.button_radius ?? 8
    }
  }
}

export const createCartUIPayload = (cartUIData: customizeCartUIType): CartUIConfigTypeForBackend => {
  const { multipleBannersEnabled, animationStyle, animationDelay, banners, color, textColor, text, enabled } =
    cartUIData.cartBannerUIConfigData

  const cartBannerSettingsData: BannerSettingsTypeForBackend = {
    enabled,
    multiple_banners_enabled: multipleBannersEnabled ?? false,
    animation_style: animationStyle ?? 'vertical_ticker',
    animation_delay: animationDelay ?? 1
  }

  const activeBanners = banners && banners.length > 0 ? banners : null
  const cartBannerConfigItems: BannerConfigTypeForBackend[] = activeBanners
    ? activeBanners.map((b, index) => ({
        enabled: enabled ?? true,
        type: 'CART' as const,
        position: b.position ?? index,
        background_color: b.color ?? color ?? '#000000',
        text_color: b.textColor ?? textColor ?? '#FFFFFF',
        html_text_string: b.text ?? text ?? ''
      }))
    : [
        {
          enabled: enabled ?? true,
          type: 'CART' as const,
          position: 0,
          background_color: color ?? '#000000',
          text_color: textColor ?? '#FFFFFF',
          html_text_string: text ?? ''
        }
      ]

  const cartBottomBannerConfigData: BannerConfigTypeForBackend = {
    enabled: cartUIData.cartBottomBannerUIConfigData.enabled,
    type: 'CART' as const,
    background_color: cartUIData.cartBottomBannerUIConfigData.color,
    text_color: cartUIData.cartBottomBannerUIConfigData.textColor,
    html_text_string: cartUIData.cartBottomBannerUIConfigData.text,
    position: 0
  }

  const cartPrimaryCTAConfig: PrimaryCTATextTypeForBackend = {
    type: 'CART',
    cta_text: cartUIData.primaryCTAConfig.cartButtonCTAText,
    background_color: cartUIData.primaryCTAConfig.backgroundColor,
    text_color: cartUIData.primaryCTAConfig.textColor,
    border_radius: cartUIData.primaryCTAConfig.borderRadius
  }

  const cartOrderSummaryConfig: OrderSummaryConfigTypeForBackend = {
    background_color: cartUIData.orderSummaryConfig.backgroundColor,
    border_color: cartUIData.orderSummaryConfig.borderColor,
    offer_badge_color: cartUIData.orderSummaryConfig.offerBadgeColor,
    text_color: cartUIData.orderSummaryConfig.textColor,
    quantity_editor_background: cartUIData.orderSummaryConfig.quantityEditorBackground,
    quantity_editor_text: cartUIData.orderSummaryConfig.quantityEditorText,
    card_radius: cartUIData.orderSummaryConfig.cardRadius,
    card_border_width: cartUIData.orderSummaryConfig.cardBorderWidth,
    card_border_sides: cartUIData.orderSummaryConfig.cardBorderSides
  }

  const cartDiscountConfig: DiscountConfigTypeForBackend = {
    callout: {
      background_color: cartUIData.discountConfig.callout.backgroundColor,
      info_color: cartUIData.discountConfig.callout.infoColor,
      primary_color: cartUIData.discountConfig.callout.primaryColor,
      border_color: cartUIData.discountConfig.callout.borderColor
    },
    footer: {
      background_color: cartUIData.discountConfig.footer.backgroundColor,
      info_color: cartUIData.discountConfig.footer.infoColor,
      primary_color: cartUIData.discountConfig.footer.primaryColor
    },
    input: {
      background_color: cartUIData.discountConfig.input.backgroundColor,
      border_color: cartUIData.discountConfig.input.borderColor,
      primary_color: cartUIData.discountConfig.input.primaryColor
    },
    button: {
      background_color: cartUIData.discountConfig.button.backgroundColor,
      text_color: cartUIData.discountConfig.button.textColor
    },
    total_savings: {
      primary_color: cartUIData.discountConfig.totalSavings.primaryColor,
      info_color: cartUIData.discountConfig.totalSavings.infoColor,
      background_color: cartUIData.discountConfig.totalSavings.backgroundColor
    },
    card_radius: cartUIData.discountConfig.cardRadius,
    card_border_width: cartUIData.discountConfig.cardBorderWidth,
    card_border_sides: cartUIData.discountConfig.cardBorderSides
  }

  const cartUpsellConfig: UpsellConfigTypeForBackend = {
    is_enabled: cartUIData.upsell.isEnabled,
    title: cartUIData.upsell.title,
    discounted_title: cartUIData.upsell.discountedTitle,
    should_override_discounted_upsell: cartUIData.upsell.shouldOverrideDiscountedUpsell,
    background_color: cartUIData.upsell.backgroundColor,
    border_color: cartUIData.upsell.borderColor,
    text_color: cartUIData.upsell.textColor,
    button_color: cartUIData.upsell.buttonColor,
    offer_badge_color: cartUIData.upsell.offerBadgeColor,
    title_color: cartUIData.upsell.titleColor,
    card_radius: cartUIData.upsell.cardRadius,
    card_border_width: cartUIData.upsell.cardBorderWidth,
    card_border_sides: cartUIData.upsell.cardBorderSides
  }

  const cartNotesConfig: NotesConfigTypeForBackend = {
    is_enabled: cartUIData.notesConfig.isEnabled,
    background_color: cartUIData.notesConfig.backgroundColor,
    border_color: cartUIData.notesConfig.borderColor,
    text_color: cartUIData.notesConfig.textColor,
    button_color: cartUIData.notesConfig.buttonColor,
    card_radius: cartUIData.notesConfig.cardRadius,
    card_border_width: cartUIData.notesConfig.cardBorderWidth,
    card_border_sides: cartUIData.notesConfig.cardBorderSides
  }

  const addOnConfig: AddOnConfigTypeForBackend = {
    background_color: cartUIData.addonConfig.backgroundColor,
    text_color: cartUIData.addonConfig.textColor,
    button_color: cartUIData.addonConfig.buttonColor
  }

  const cartCouponPageConfig: CouponPageConfigTypeForBackend = {
    background_color: cartUIData.couponPageConfig.backgroundColor,
    text_color: cartUIData.couponPageConfig.textColor,
    primary_color: cartUIData.couponPageConfig.primaryColor,
    card_radius: cartUIData.couponPageConfig.cardRadius,
    filter_radius: cartUIData.couponPageConfig.filterRadius,
    button_radius: cartUIData.couponPageConfig.buttonRadius
  }

  return {
    page: {
      background_color: cartUIData.page.backgroundColor
    },
    layout: {
      enable_marketing_flag: cartUIData.enableMarketingFlag ?? false
    },
    header: {
      background_color: cartUIData.header.backgroundColor,
      text_color: cartUIData.header.textColor
    },
    footer: {
      background_color: cartUIData.footer.backgroundColor,
      text_color: cartUIData.footer.textColor,
      savings_color: cartUIData.footer.savingsColor,
      show_savings: cartUIData.footer.showSavings
    },
    primary_cta_config: [cartPrimaryCTAConfig],
    banner_settings: cartBannerSettingsData,
    banner_config: cartBannerConfigItems,
    bottom_banner_config: [cartBottomBannerConfigData],
    cart_overlay_width: cartUIData.cartWidthForMobile,
    coupon_visibility_config: cartUIData.couponVisibilityConfig.map((config: CouponVisibilityStateConfigType) => {
      return {
        visibile: config.areCouponsVisible,
        visibility_extent: config.visibilityExtent,
        type: config.type,
        hide_savings: config.hideSavings
      }
    }),
    show_upi_icons_on_cart_button: cartUIData.showUpiIconsOnCartButton,
    order_summary_config: cartOrderSummaryConfig,
    discount_config: cartDiscountConfig,
    upsell: cartUpsellConfig,
    notes_config: cartNotesConfig,
    addon_config: addOnConfig,
    coupon_page_config: cartCouponPageConfig,
    element_order: cartUIData.elementOrder
  }
}

export const parseGenericWidgetData = (
  responses: any[],
  uiConfig: GenericAnalyticsWidgetUIConfigType
): GenericAnalyticsWidgetUIConfigType => {
  const currentPeriodData = responses?.[0]?.widgetData
  const comparisonPeriodData = responses?.[1]?.widgetData
  if (comparisonPeriodData) {
    return {
      ...uiConfig,
      ...(uiConfig?.primaryMetricList
        ? {
            primaryMetricList: uiConfig.primaryMetricList.map(
              (primaryMetric: AnalyticsMetricType): AnalyticsMetricType => {
                const currentValue = Number(currentPeriodData?.cart_conversion_percentage) ?? 0
                const previousValue = Number(comparisonPeriodData?.cart_conversion_percentage) ?? 0
                const comparePercentage = () => {
                  if (!previousValue) return 0
                  const percentage = Math.abs(((currentValue - previousValue) / previousValue) * 100)
                  const result = isNaN(percentage) || !isFinite(percentage) ? 0 : Number(percentage.toFixed(2))
                  return result
                }
                return {
                  ...primaryMetric,
                  title: currentPeriodData
                    ? `${currentPeriodData.total_checkout_initiated ?? 0} of ${
                        currentPeriodData.total_cart_clicked ?? 0
                      } carts converted to checkouts`
                    : primaryMetric.title,
                  value: currentValue,
                  compareStatus: !comparisonPeriodData
                    ? 'equal'
                    : currentValue > previousValue
                    ? 'increased'
                    : currentValue < previousValue
                    ? 'decreased'
                    : 'equal',
                  comparePercentage: comparePercentage(),
                  compareValue: previousValue
                }
              }
            )
          }
        : {}),
      ...(uiConfig?.secondaryMetricsList
        ? {
            secondaryMetricsList: uiConfig.secondaryMetricsList.map(
              (secondaryMetric: AnalyticsMetricType): AnalyticsMetricType => ({
                ...secondaryMetric,
                value:
                  secondaryMetric.title === 'cart created'
                    ? currentPeriodData?.total_cart_clicked ?? '0'
                    : currentPeriodData?.total_checkout_initiated ?? 0
              })
            )
          }
        : {})
    }
  }
  return {
    ...uiConfig,
    ...(uiConfig?.primaryMetricList
      ? {
          primaryMetricList: uiConfig.primaryMetricList.map(
            (primaryMetric: AnalyticsMetricType): AnalyticsMetricType => {
              const currentValue = Number(currentPeriodData?.cart_conversion_percentage) ?? 0
              return {
                ...primaryMetric,
                title: currentPeriodData
                  ? `${currentPeriodData.total_checkout_initiated ?? 0} of ${
                      currentPeriodData.total_cart_clicked ?? 0
                    } carts converted to checkouts`
                  : primaryMetric.title,
                value: currentValue
              }
            }
          )
        }
      : {}),
    ...(uiConfig?.secondaryMetricsList
      ? {
          secondaryMetricsList: uiConfig.secondaryMetricsList.map(
            (secondaryMetric: AnalyticsMetricType): AnalyticsMetricType => ({
              ...secondaryMetric,
              value:
                secondaryMetric.title === 'cart created'
                  ? currentPeriodData?.total_cart_clicked ?? '0'
                  : currentPeriodData?.total_checkout_initiated ?? 0
            })
          )
        }
      : {})
  }
}

export const parseCartConversionChartData = (
  responses: any[],
  uiConfig: GenericAnalyticsWidgetUIConfigType
): GenericAnalyticsWidgetUIConfigType => {
  const currentPeriodData = responses?.[0]?.widgetData ?? []
  const comparisonPeriodData = responses?.[1]?.widgetData ?? []

  const currentConversionData =
    currentPeriodData.find((item: any) => item.dimension === 'prev_step_percentage')?.data ?? []

  const comparisonConversionData =
    comparisonPeriodData.find((item: any) => item.dimension === 'prev_step_percentage')?.data ?? []

  const currentEventsData = currentPeriodData.find((item: any) => item.dimension === 'events_count')?.data ?? []

  const comparisonEventsData = comparisonPeriodData.find((item: any) => item.dimension === 'events_count')?.data ?? []
  const currentValue = currentConversionData?.[0]?.checkout_initiated_vs_added_to_cart_ui ?? 0
  const previousValue = comparisonConversionData?.[0]?.checkout_initiated_vs_added_to_cart_ui ?? 0

  return {
    ...uiConfig,
    chartData: {
      current: currentConversionData,
      comparison: comparisonConversionData
    },
    ...(uiConfig?.primaryMetricList
      ? {
          primaryMetricList: uiConfig.primaryMetricList.map(
            (primaryMetric: AnalyticsMetricType): AnalyticsMetricType => {
              return {
                ...primaryMetric,
                value: currentValue,
                title: `Cart Conversions`,
                compareStatus: !comparisonPeriodData.length
                  ? 'equal'
                  : currentValue > previousValue
                  ? 'increased'
                  : currentValue < previousValue
                  ? 'decreased'
                  : 'equal',
                comparePercentage:
                  previousValue === 0 || currentValue === 0
                    ? 0
                    : Number(Math.abs(((currentValue - previousValue) / previousValue) * 100).toFixed(2)),
                compareValue: previousValue
              }
            }
          )
        }
      : {}),
    ...(uiConfig?.secondaryMetricsList
      ? {
          secondaryMetricsList: uiConfig.secondaryMetricsList.map(
            (secondaryMetric: AnalyticsMetricType): AnalyticsMetricType => ({
              ...secondaryMetric,
              value: currentEventsData
            })
          )
        }
      : {})
  }
}
export const parseCartMilestones = (responses: any[]): any => {
  const widgetData = responses?.[0]?.widgetData ?? []
  const finalData = widgetData.map((milestone: any) => ({
    amount: milestone.milestone_amount_type === 'PRICE_BASED' ? `₹${milestone.amount}` : milestone.amount,
    milestoneSessionCount: milestone.milestone_session_count,
    eventSessionCount: milestone.event_session_count,
    conversionPercentage: milestone.conversion_percentage,
    milestoneHeader: milestone.milestone_header,
    type: milestone.milestone_amount_type
  }))
  return finalData
}
const parseComparisonParams = (filters: FilterConfigType, selectedTimeScale: string): string | null => {
  if (filters.comparison === 'previous') {
    const startDate = new Date(extractDateFromFilters(filters, 'startDate', 'UTC'))
    const endDate = new Date(extractDateFromFilters(filters, 'endDate', 'UTC'))
    if (startDate && endDate) {
      const currentStartDate = addDays(startDate, 1)
      const currentEndDate = addDays(endDate, 1)
      const diffInDays = Math.ceil((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24))

      const previousStartDate = endOfDay(addDays(currentStartDate, -diffInDays))
      const previousEndDate = endOfDay(addDays(currentEndDate, -diffInDays))

      const url = `?timeZone=Asia%2FCalcutta&start_date=${encodeURIComponent(
        previousStartDate.toISOString()
      )}&end_date=${encodeURIComponent(previousEndDate.toISOString())}&time_scale=${selectedTimeScale}`

      return url
    }
  }
  if (filters.compareToStartDate && filters.compareToEndDate) {
    const previousStartDate = extractDateFromFilters(filters, 'compareToStartDate')
    const previousEndDate = extractDateFromFilters(filters, 'compareToEndDate')
    return `?timeZone=Asia%2FCalcutta&start_date=${encodeURIComponent(previousStartDate)}&end_date=${encodeURIComponent(
      previousEndDate
    )}&time_scale=${selectedTimeScale}`
  }
  return null
}
const dateParser = (date: string) => {
  return `${date.split('T')[0]} 18:30:00`
}
export const CartAnalyticsConfig = (
  merchantId: string,
  filters: FilterConfigType,
  selectedTimeScale: string
): any[] => {
  const payload = {
    interval: {
      start: extractDateFromFilters(filters, 'startDate', 'UTC_TIMEZONE'),
      end: extractDateFromFilters(filters, 'endDate', 'UTC_TIMEZONE')
    },
    ...(filters.comparison !== 'none'
      ? {
          comparison_interval: {
            start: extractDateFromFilters(filters, 'compareToStartDate', 'UTC_TIMEZONE'),
            end: extractDateFromFilters(filters, 'compareToEndDate', 'UTC_TIMEZONE')
          }
        }
      : {}),
    difference_type: 'PERCENT'
  }

  const baseURL = `/dashboard/cartWidget/`
  const start_date = payload.interval.start
  const end_date = payload.interval.end
  const queryParams = `?timeZone=Asia%2FCalcutta&start_date=${extractDateFromFilters(
    filters,
    'startDate'
  )}&end_date=${extractDateFromFilters(filters, 'endDate')}&time_scale=${selectedTimeScale}`
  const currentParams = `?timeZone=Asia%2FCalcutta&start_date=${extractDateFromFilters(
    filters,
    'startDate'
  )}&end_date=${extractDateFromFilters(filters, 'endDate')}&time_scale=${selectedTimeScale}`
  const comparisonParams = parseComparisonParams(filters, selectedTimeScale)
  return [
    {
      type: cartAnalyticsWidgetSet.GENERIC_WIDGET,
      data: {
        uiConfig: {
          id: 'cart-conversion',
          positionX: 0,
          positionY: 0,
          title: '',
          primaryMetricList: [
            {
              title: 'checkouts converted',
              header: 'Carts converted to checkout',
              type: 'PERCENTAGE',
              value: 0
            }
          ],
          secondaryMetricsList: [
            {
              title: 'cart created',
              header: 'cart created',
              type: 'NUMBER',
              value: 0
            },
            {
              title: 'checkout initiated',
              header: 'checkout initiated',
              type: 'NUMBER',
              value: 0
            }
          ]
        },
        requestConfig: {
          endpoints:
            comparisonParams && filters.comparison !== 'none'
              ? [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion${currentParams}`
                  },
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion${comparisonParams}`
                  }
                ]
              : [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion${currentParams}`
                  }
                ],
          filters: filters,
          parser: parseGenericWidgetData
        }
      }
    },
    {
      type: cartAnalyticsWidgetSet.CART_CONVERSION_CHART,
      data: {
        uiConfig: {
          id: 'cart-conversion',
          positionX: 0,
          positionY: 0,
          title: '',
          primaryMetricList: [
            {
              title: 'Cart Conversion',
              header: 'Cart Conversions',
              type: 'PERCENTAGE',
              value: 0
            }
          ],
          secondaryMetricsList: [
            {
              title: 'cart created',
              header: 'cart created',
              type: 'NUMBER',
              value: 0
            }
          ]
        },
        requestConfig: {
          endpoints:
            comparisonParams && filters.comparison !== 'none'
              ? [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion_chart${currentParams}`
                  },
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion_chart${comparisonParams}`
                  }
                ]
              : [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${baseURL}cart_conversion_chart${currentParams}`
                  }
                ],
          filters: filters,
          parser: parseCartConversionChartData
        }
      }
    },
    {
      type: cartAnalyticsWidgetSet.CART_MILESTONES,
      data: {
        uiConfig: {
          id: 'milestone_cart',
          positionX: 0,
          positionY: 0,
          title: 'Milestone Cart',
          primaryMetricList: [
            {
              title: 'Milestone Conversion',
              header: 'Milestone Conversion Rate',
              type: 'PERCENTAGE',
              value: 0
            }
          ],
          secondaryMetricsList: [
            {
              title: 'Milestone Session Count',
              header: 'Milestone Sessions',
              type: 'NUMBER',
              value: 0
            },
            {
              title: 'Event Session Count',
              header: 'Event Sessions',
              type: 'NUMBER',
              value: 0
            }
          ]
        },
        requestConfig: {
          endpoints: [
            {
              baseURL: apiURI.CHECKOUT_NODE ?? '',
              requestType: analyticsApiRequestType.GET,
              url: `${baseURL}milestone_cart${queryParams}`
            }
          ],
          filters: filters,
          parser: parseCartMilestones
        }
      }
    }
  ]
}

export const getBorderColor = (backgroundColor: string): string => {
  return hexToRGBA(backgroundColor.substring(0.7), 0.5)
}
export const getScaleUnit = (timeScale: string): 'hour' | 'day' | 'month' | undefined => {
  switch (timeScale) {
    case 'daily':
      return 'day'
    case 'hourly':
      return 'hour'
    case 'monthly':
      return 'month'
    default:
      return 'day'
  }
}

export const getTabName = (pathname: string): string => {
  switch (pathname) {
    case 'widgets':
      return 'Widgets'
    case 'ui':
      return 'Customize UI'
    case 'cart-analytics':
      return 'Cart Analytics'
    default:
      return 'Widgets'
  }
}

export const createCartNotePayload = (cartNotes: CartNoteType) => {
  return {
    attributes: {
      cart_note: {
        enabled: cartNotes.enabled,
        metadata: {
          icon: cartNotes.icon,
          title: cartNotes.title,
          suggestion_text: cartNotes.suggestionText
        }
      }
    }
  }
}

export const handleCartNoteErrors = (cartNotes: CartNoteType) => {
  const cartNoteErrors = { ...DEFAULT_CART_NOTE_ERROR }

  if (!cartNotes.title.trim()) {
    cartNoteErrors.title = {
      hasError: true,
      errorMessage: 'Title cannot be empty'
    }
  }

  if (!cartNotes.suggestionText.trim()) {
    cartNoteErrors.suggestionText = {
      hasError: true,
      errorMessage: 'Suggestion message cannot be empty'
    }
  }

  const hasError = cartNoteErrors.title.hasError || cartNoteErrors.suggestionText.hasError

  return {
    cartNoteErrors,
    hasError
  }
}

export const normalizeBannerConfig = (rawData: any): BannerConfigType => {
  const bannerSettings = rawData?.banner_settings ?? {}
  const bannerConfig = rawData?.banner_config ?? []

  let banners: BannerItemConfigType[]

  if (Array.isArray(bannerConfig) && bannerConfig.length > 0) {
    banners = bannerConfig.map((item: any, index: number) => ({
      id: item.id ?? `banner-${index}`,
      position: item.position ?? index,
      color: item.background_color ?? '#000000',
      textColor: item.text_color ?? '#FFFFFF',
      text: item.html_text_string ?? ''
    }))
  } else {
    // Legacy single-object banner format
    banners = [
      {
        id: 'banner-0',
        position: 0,
        color: bannerConfig.background_color ?? '#000000',
        textColor: bannerConfig.text_color ?? '#FFFFFF',
        text: bannerConfig.html_text_string ?? ''
      }
    ]
  }

  return {
    ...bannerSettings,
    banners,
    color: banners[0]?.color ?? '#000000',
    textColor: banners[0]?.textColor ?? '#FFFFFF',
    text: banners[0]?.text ?? ''
  }
}
